import { createContext, PropsWithChildren, useEffect, useCallback, useState, useRef, useMemo } from 'react';
import { throttle } from 'lodash';

import { Search } from '@mui/icons-material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import { InputAdornment, TextField } from '@mui/material';
import { useContainer } from 'unstated-next';
import { GET_CLOUDWATCH_LOGS } from '../../config/apiPaths';
import { processApiPath } from '../../utils/url';
import AdminAuthContainer from './AdminAuthContainer';
import { getUserUuidHeaders } from '../../utils/httpUtils';

export interface CloudWatchLogProviderProps {
    primaryQuery: string;
    secondaryQuery: string | undefined;
    startTime: number;
    endTime: number;
    setSearchText?: (s: string) => void;
    style: any;
    addSearchBox?: boolean;
}

interface CloudWatchLogContextInterface {
    isLoading: boolean;
    cloudWatchLogs: any[];
    setCloudWatchLogs: (c: any[]) => void;
    startTime: number;
    endTime: number;
    allLoaded: boolean;
    setSearchText?: (s: string) => void;
    addSearchBox?: boolean;
}

const CloudWatchLogContext = createContext(undefined as unknown as CloudWatchLogContextInterface);

const { Provider, Consumer } = CloudWatchLogContext;

const CloudWatchLogProvider = (props: PropsWithChildren<CloudWatchLogProviderProps>) => {
    const { t } = useTranslation('class-sessions');
    const search = new URLSearchParams(window.location.search);
    const userUuid = search.get('user_uuid') || window.localStorage.getItem('user_uuid');

    const { authMode, showDemoContent } = useContainer(AdminAuthContainer);
    const { primaryQuery, secondaryQuery, startTime, endTime, setSearchText, addSearchBox, children, style } = props;
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [allLoaded, setAllLoaded] = useState<boolean>(false);
    const [internalSecondaryQuery, setInternalSecondaryQuery] = useState<string>('');
    const [cloudWatchLogs, setCloudWatchLogs] = useState<any[]>([]);
    const startTimeRef = useRef(startTime);
    const cloudWatchLogsRef = useRef(cloudWatchLogs);
    const containerRef = useRef(null);
    const secondaryQueryRef = useRef(secondaryQuery);
    secondaryQueryRef.current = secondaryQuery;

    useEffect(() => {
        startTimeRef.current = startTime;
    }, [startTime]);

    useEffect(() => {
        cloudWatchLogsRef.current = cloudWatchLogs;
    }, [cloudWatchLogs]);

    const handleSecondaryQueryChange = useMemo(
        () => throttle(() => {
            console.log('throttle run');
            setCloudWatchLogs([]);
            setAllLoaded(false);
            setIsLoading(true);
        }, 1000),
        []
    );

    useEffect(() => {
        secondaryQueryRef.current = internalSecondaryQuery || secondaryQuery;
        console.log('throttle called');
        setIsLoading(false);
        handleSecondaryQueryChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [secondaryQuery, internalSecondaryQuery]);

    const getTempStartTime = useCallback(async () => {
        let tempStartTime = startTimeRef.current;
        if (cloudWatchLogsRef.current.length > 0) {
            tempStartTime = cloudWatchLogsRef.current[cloudWatchLogsRef.current.length - 1].timestamp + 1;
        }
        return tempStartTime;
    }, []);

    const appendCloudWatchLogs = useCallback(async (logData: any[]) => {
        if (!logData || logData.length === 0) {
            setAllLoaded(true);
        } else {
            setCloudWatchLogs((prevLogs) => prevLogs.concat(logData));
        }
    }, []);

    const handleChangeSearch = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setInternalSecondaryQuery(event.target.value);
    };

    useEffect(() => {
        const loadLogsInner = async () => {
            console.log('isLoading', isLoading);
            if (!isLoading || allLoaded) {
                return;
            }
            const tempStartTime = await getTempStartTime();
            if (tempStartTime >= endTime) {
                setIsLoading(false);
                setAllLoaded(true);
                return;
            }
            const { data: logData } = await axios.get(
                processApiPath(GET_CLOUDWATCH_LOGS, undefined, {
                    query: `${primaryQuery}`,
                    secondary_query: `${internalSecondaryQuery || secondaryQuery}`,
                    auth_mode: authMode,
                    demo_content: String(showDemoContent),
                    start_time: String(tempStartTime),
                    end_time: String(endTime)
                }), { headers: getUserUuidHeaders(userUuid as string) }
            );
            if ((internalSecondaryQuery || secondaryQuery) !== secondaryQueryRef.current) {
                console.log('secondaryQuery', secondaryQuery, 'secondaryQueryRef', secondaryQueryRef, 'secondaryQueryRef.current', secondaryQueryRef.current);
                console.log('secondaryQuery changed out from under us');
                return;
            }
            appendCloudWatchLogs(logData);
            setIsLoading(false);
        };
        loadLogsInner();
    }, [authMode, primaryQuery, secondaryQuery, internalSecondaryQuery, userUuid, showDemoContent, startTime, endTime, getTempStartTime, allLoaded, appendCloudWatchLogs, isLoading]);

    useEffect(() => {
        function handleScroll() {
            if (containerRef.current) {
                const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
                if (scrollTop + clientHeight >= scrollHeight) {
                    setIsLoading(true);
                }
            }
        }

        if (containerRef.current) {
            const element = containerRef.current as HTMLElement;
            element.addEventListener('scroll', handleScroll);

            return () => {
                element.removeEventListener('scroll', handleScroll);
            };
        }
    }, [containerRef]);

    return (
        <Provider
            value={{
                cloudWatchLogs,
                setCloudWatchLogs,
                isLoading,
                startTime,
                endTime,
                allLoaded,
                setSearchText,
                addSearchBox
            }}
        >
            {addSearchBox && <TextField
                label={t('search-placeholder')}
                value={internalSecondaryQuery}
                onChange={handleChangeSearch}
                size='small'
                variant='standard'
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Search />
                        </InputAdornment>
                    )
                }}
            />}
            <div ref={containerRef} style={{ ...style, overflowY: 'auto' }}>
                {children}
            </div>
        </Provider>
    );
};

export { CloudWatchLogContext, CloudWatchLogProvider, Consumer as CloudWatchLogConsumer };
